import React, { useState, useEffect } from "react";
import { SchoolConfig } from "../types/school";
import LoginPanel from "./LoginPanel";
import Footer from "./Footer";

const SESSION_TOKEN_KEY = "edunexi_portal_access_token";
const SESSION_REFRESH_KEY = "edunexi_portal_refresh_token";
const SESSION_ROLE_KEY = "edunexi_portal_role";
const SESSION_TENANT_KEY = "edunexi_portal_tenant_key";
const SESSION_USERNAME_KEY = "edunexi_portal_username";
const SESSION_REDIRECT_KEY = "edunexi_portal_redirect_url";

function buildRedirectUrl(
  redirectBase: string,
  token: string,
  refreshToken: string,
  role: string,
  tenantKey: string,
  username: string,
) {
  const params = new URLSearchParams({
    token,
    refreshToken,
    role,
    tenantKey,
    username,
    // Let frontend-react know the portal origin so it can redirect back here on logout.
    portalUrl: window.location.origin,
  });
  return `${redirectBase}/login?${params.toString()}`;
}

interface LoginPageProps {
  config: SchoolConfig;
}

const LoginPage: React.FC<LoginPageProps> = ({ config }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  // On mount: handle logout signal or check for a saved session
  useEffect(() => {
    // If frontend-react redirected back here with ?logout=true, clear the portal
    // session so the user sees the login form instead of being auto-redirected.
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("logout") === "true") {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(SESSION_REFRESH_KEY);
      localStorage.removeItem(SESSION_ROLE_KEY);
      localStorage.removeItem(SESSION_TENANT_KEY);
      localStorage.removeItem(SESSION_USERNAME_KEY);
      localStorage.removeItem(SESSION_REDIRECT_KEY);
      // Clean the URL so a refresh doesn't re-trigger the logout clear.
      window.history.replaceState({}, "", window.location.pathname);
      setSessionChecked(true);
      return;
    }

    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    const refreshToken = localStorage.getItem(SESSION_REFRESH_KEY) ?? "";
    const role = localStorage.getItem(SESSION_ROLE_KEY);
    const tenantKey = localStorage.getItem(SESSION_TENANT_KEY);
    const username = localStorage.getItem(SESSION_USERNAME_KEY) ?? "";
    const savedRedirect =
      localStorage.getItem(SESSION_REDIRECT_KEY) ?? config.redirectUrl;

    if (token && role && tenantKey) {
      // Existing session found — redirect to the main app with the saved tokens.
      // The main app's api-client will silently refresh if the access token is expired.
      window.location.href = buildRedirectUrl(
        savedRedirect,
        token,
        refreshToken,
        role,
        tenantKey,
        username,
      );
      return;
    }

    setSessionChecked(true);
  }, [config.redirectUrl]);

  const handleAuthSuccess = (authData: any) => {
    const { accessToken, refreshToken, user } = authData;

    // Persist the session so future visits skip the login form
    localStorage.setItem(SESSION_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(SESSION_REFRESH_KEY, refreshToken);
    localStorage.setItem(SESSION_ROLE_KEY, user.role);
    localStorage.setItem(SESSION_TENANT_KEY, config.tenantId);
    localStorage.setItem(SESSION_USERNAME_KEY, user.username);
    localStorage.setItem(SESSION_REDIRECT_KEY, config.redirectUrl);

    // Redirect to the main app with auth parameters
    window.location.href = buildRedirectUrl(
      config.redirectUrl,
      accessToken,
      refreshToken ?? "",
      user.role,
      config.tenantId,
      user.username,
    );
  };

  const handleAuthError = (error: Error) => {
    setError(error.message);
    setIsLoading(false);
    setTimeout(() => setError(null), 5000);
  };

  const handleAuthStart = () => {
    setIsLoading(true);
    setError(null);
  };

  // Don't render the login form until we've confirmed there's no saved session
  if (!sessionChecked) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-slate-900 to-blue-900 flex flex-col">
      {/* Main Glass Container */}
      <div className="flex-1 flex items-center justify-center p-3 lg:p-6">
        <div className="w-full max-w-5xl bg-white/10 backdrop-blur-2xl rounded-[24px] overflow-hidden shadow-2xl border border-white/20 grid lg:grid-cols-2 h-[calc(100vh-6rem)] max-h-[580px]">
          {/* Left Hero Section - Simplified */}
          <div className="relative hidden lg:flex items-center justify-center bg-gradient-to-br from-blue-700/60 to-indigo-900/70">
            {/* Floating circles */}
            <div className="absolute top-8 left-8 w-32 h-32 bg-cyan-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-8 right-8 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl"></div>

            <div className="relative z-10 px-8">
              {/* School Image Only */}
              <img
                src={
                  config.images.heroImage ||
                  config.images.backgroundImage ||
                  "https://images.unsplash.com/photo-1509062522246-3755977927d7"
                }
                alt="School"
                className="rounded-2xl shadow-xl object-cover h-[280px] w-full"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "https://images.unsplash.com/photo-1509062522246-3755977927d7";
                }}
              />
            </div>
          </div>

          {/* Right Login Section */}
          <div className="bg-white p-6 lg:p-8 flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="mb-6 text-center">
                <img
                  src={config.images.logo}
                  alt={config.name}
                  className="h-12 w-auto mx-auto mb-4"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                  }}
                />
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  Welcome Back
                </h1>
                <p className="text-slate-500">
                  Login to continue to your school portal
                </p>
              </div>

              <LoginPanel
                config={config}
                isLoading={isLoading}
                error={error}
                onAuthSuccess={handleAuthSuccess}
                onAuthError={handleAuthError}
                onAuthStart={handleAuthStart}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <Footer config={config} />
    </div>
  );
};

export default LoginPage;
